/** @generated Vendored from @mauriciobenjamin700/ort-vision-sdk-web. Do not hand-edit — regenerate with `npm run vendor:vision`. */
/**
 * Per-image result envelopes — Ultralytics-style `Results` for the SDK.
 *
 * Each `predict()` call returns a 1-element array of these envelopes,
 * mirroring `YOLO("img.jpg")`. Each envelope holds:
 *
 * - A bulk-array view of the predictions (`Boxes`, `Probs`, `Masks`) with
 *   the exact attribute names Ultralytics uses (`xyxy`, `xywh`, `xyxyn`,
 *   `xywhn`, `cls`, `conf`, `data`, `top1`, `top5`).
 * - Per-instance dataclasses (`DetectionResult`, `SegmentationResult`,
 *   `ClassProbability`) for callers who prefer the OO interface.
 * - `names`: `Record<number, string>` matching Ultralytics' `model.names`.
 * - `origImg` / `origShape` / `path`: provenance for the original input.
 * - `speed`: per-stage timings of the `predict()` call that produced it.
 */

import { type Speed } from "./core/timing";
import {
    type ClassificationResult,
    type DetectionResult,
    type RGBImage,
    type SegmentationResult,
} from "./types";

/**
 * Zero timings for an envelope built outside a `predict()` call.
 *
 * Hand-constructed envelopes (tests, adapters) have nothing to measure, and
 * zeros keep `speed.inference` a plain `number` at every call site instead of
 * forcing an optional check that only ever fires for synthetic data.
 */
const NO_SPEED: Speed = { load: 0, preprocess: 0, inference: 0, postprocess: 0 };

/**
 * Bulk numpy-style view of detected boxes for a single image.
 *
 * Mirrors Ultralytics' `Boxes` interface. Coordinates in {@link xyxy} and
 * {@link xywh} are absolute pixels in the original image; the `*n` variants
 * are normalized to `[0, 1]` using `origShape`.
 */
export class Boxes {
    /**
     * @param xyxy Flat array of length `4 * N` in `[x1, y1, x2, y2, ...]` order.
     * @param cls One class index per box, length `N`.
     * @param conf One confidence per box, length `N`.
     * @param origShape `[height, width]` of the original image.
     */
    constructor(
        public readonly xyxy: Float32Array,
        public readonly cls: Int32Array,
        public readonly conf: Float32Array,
        public readonly origShape: readonly [number, number],
    ) {}

    /** Number of detected boxes. */
    get length(): number {
        return this.cls.length;
    }

    /** `[N, 4]` shape of the `xyxy` view. */
    get shape(): readonly [number, number] {
        return [this.length, 4];
    }

    /** Boxes as `[N, 4]` `[cx, cy, w, h]` flat array in absolute pixels. */
    get xywh(): Float32Array {
        const out = new Float32Array(this.xyxy.length);
        for (let i = 0; i < this.length; i++) {
            const x1 = this.xyxy[i * 4] as number;
            const y1 = this.xyxy[i * 4 + 1] as number;
            const x2 = this.xyxy[i * 4 + 2] as number;
            const y2 = this.xyxy[i * 4 + 3] as number;
            out[i * 4] = (x1 + x2) / 2;
            out[i * 4 + 1] = (y1 + y2) / 2;
            out[i * 4 + 2] = x2 - x1;
            out[i * 4 + 3] = y2 - y1;
        }
        return out;
    }

    /** Boxes as `[N, 4]` `[x1, y1, x2, y2]` normalized to `[0, 1]`. */
    get xyxyn(): Float32Array {
        const [h, w] = this.origShape;
        const out = new Float32Array(this.xyxy.length);
        if (this.length === 0 || w <= 0 || h <= 0) return out;
        for (let i = 0; i < this.length; i++) {
            out[i * 4] = (this.xyxy[i * 4] as number) / w;
            out[i * 4 + 1] = (this.xyxy[i * 4 + 1] as number) / h;
            out[i * 4 + 2] = (this.xyxy[i * 4 + 2] as number) / w;
            out[i * 4 + 3] = (this.xyxy[i * 4 + 3] as number) / h;
        }
        return out;
    }

    /** Boxes as `[N, 4]` `[cx, cy, w, h]` normalized to `[0, 1]`. */
    get xywhn(): Float32Array {
        const xywh = this.xywh;
        const [h, w] = this.origShape;
        if (this.length === 0 || w <= 0 || h <= 0) return xywh;
        for (let i = 0; i < this.length; i++) {
            xywh[i * 4] = (xywh[i * 4] as number) / w;
            xywh[i * 4 + 1] = (xywh[i * 4 + 1] as number) / h;
            xywh[i * 4 + 2] = (xywh[i * 4 + 2] as number) / w;
            xywh[i * 4 + 3] = (xywh[i * 4 + 3] as number) / h;
        }
        return xywh;
    }

    /**
     * Concatenated `[N, 6]` array of `[x1, y1, x2, y2, conf, cls]`.
     *
     * Matches Ultralytics' `boxes.data`.
     */
    get data(): Float32Array {
        const out = new Float32Array(this.length * 6);
        for (let i = 0; i < this.length; i++) {
            out[i * 6] = this.xyxy[i * 4] as number;
            out[i * 6 + 1] = this.xyxy[i * 4 + 1] as number;
            out[i * 6 + 2] = this.xyxy[i * 4 + 2] as number;
            out[i * 6 + 3] = this.xyxy[i * 4 + 3] as number;
            out[i * 6 + 4] = this.conf[i] as number;
            out[i * 6 + 5] = this.cls[i] as number;
        }
        return out;
    }
}

/** One selection of the highest-probability classes, descending. */
interface TopK {
    indices: Int32Array;
    values: Float32Array;
}

/**
 * Top-k classification probabilities for a single image.
 *
 * Mirrors Ultralytics' `Probs` interface.
 */
export class Probs {
    /** @param data `[numClasses]` per-class probabilities, indexed by class id. */
    constructor(public readonly data: Float32Array) {}

    /** Number of classes. */
    get length(): number {
        return this.data.length;
    }

    /** `[numClasses]` shape of the underlying vector. */
    get shape(): readonly [number] {
        return [this.length];
    }

    /** Index of the most probable class. */
    get top1(): number {
        if (this.data.length === 0) return 0;
        let best = 0;
        let bestVal = this.data[0] as number;
        for (let i = 1; i < this.data.length; i++) {
            const v = this.data[i] as number;
            if (v > bestVal) {
                best = i;
                bestVal = v;
            }
        }
        return best;
    }

    /** Probability of the top-1 class. */
    get top1conf(): number {
        if (this.data.length === 0) return 0;
        return this.data[this.top1] as number;
    }

    /**
     * Indices of the top-5 most probable classes, descending.
     *
     * The array is the memoised selection itself, not a copy — reading it twice
     * hands back the same object. Treat it as read-only: writing into it edits
     * what every later read of this `Probs` returns.
     */
    get top5(): Int32Array {
        return this._top(5).indices;
    }

    /**
     * Probabilities of the top-5 classes, descending.
     *
     * Shares the memoised selection with {@link Probs.top5}, under the same
     * read-only caveat.
     */
    get top5conf(): Float32Array {
        return this._top(5).values;
    }

    private _cache = new Map<number, TopK>();

    /**
     * Memoised {@link Probs._topK}.
     *
     * `top5` and `top5conf` are separate getters over the same selection, so a
     * caller reading both would otherwise pay for it twice — once per frame, in
     * a camera loop. The probabilities a `Probs` was built from do not change,
     * so the result is computed once per `k` and kept.
     *
     * @param k - How many classes to select.
     * @returns The cached selection for that `k`.
     */
    private _top(k: number): TopK {
        const hit = this._cache.get(k);
        if (hit) return hit;
        const computed = this._topK(k);
        this._cache.set(k, computed);
        return computed;
    }

    /**
     * Select the `k` highest probabilities without ordering the rest.
     *
     * A full sort to read five entries out of a thousand-class vector costs
     * O(n log n) plus an index array the size of the vector; keeping `k` slots
     * ordered by insertion and scanning once costs O(n·k) with no allocation
     * beyond the result. Measured on 1000 classes over 2000 iterations, 134.5 µs
     * against 1.5 µs for the same output.
     *
     * Ties keep the lower class index first, matching the stable sort this
     * replaced: a candidate only displaces an entry it is strictly greater
     * than. That is also what keeps this selection in step with the Python
     * SDK's `np.argsort(-data, kind="stable")`, where the sort is C and the
     * cost this avoids does not arise.
     *
     * @param k - How many classes to select.
     * @returns Indices and probabilities, descending by probability.
     */
    private _topK(k: number): TopK {
        const size = Math.min(k, this.data.length);
        const indices = new Int32Array(size);
        const values = new Float32Array(size);
        if (size === 0) return { indices, values };

        values.fill(Number.NEGATIVE_INFINITY);
        for (let i = 0; i < this.data.length; i++) {
            const value = this.data[i] as number;
            if (value <= (values[size - 1] as number)) continue;
            let slot = size - 1;
            while (slot > 0 && (values[slot - 1] as number) < value) {
                values[slot] = values[slot - 1] as number;
                indices[slot] = indices[slot - 1] as number;
                slot -= 1;
            }
            values[slot] = value;
            indices[slot] = i;
        }
        return { indices, values };
    }
}

/**
 * Per-instance binary masks for a single image.
 *
 * Each mask is cropped to its instance's bounding box. To paint masks onto
 * a full-image canvas, use `xyxy[i]` as the top-left target.
 */
export class Masks {
    /**
     * @param data Per-instance binary masks (`Mask` objects from `types.ts`).
     * @param xyxy Flat `[N, 4]` of bounding-box coordinates in original pixels.
     * @param origShape `[height, width]` of the original image.
     */
    constructor(
        public readonly data: ReadonlyArray<{
            readonly data: Uint8Array;
            readonly width: number;
            readonly height: number;
        }>,
        public readonly xyxy: Float32Array,
        public readonly origShape: readonly [number, number],
    ) {}

    /** Number of instance masks. */
    get length(): number {
        return this.data.length;
    }

    /** `[N]` shape of the masks collection. */
    get shape(): readonly [number] {
        return [this.length];
    }

    [Symbol.iterator](): Iterator<{
        readonly data: Uint8Array;
        readonly width: number;
        readonly height: number;
    }> {
        return this.data[Symbol.iterator]();
    }
}

/**
 * Per-image detection envelope (Ultralytics-style `Results`).
 *
 * Iterating yields per-instance {@link DetectionResult} entries, so legacy
 * code that did `for (const d of detector.predict(img))` only needs an
 * extra `[0]` to bridge:
 *
 * ```typescript
 * for (const d of (await detector.predict(img))[0]) {
 *   console.log(d.cls, d.conf, d.box.xyxy);
 * }
 * ```
 *
 * For numpy-style bulk access, use the `boxes` collection.
 */
export class DetectionResults implements Iterable<DetectionResult> {
    constructor(
        public readonly boxes: Boxes,
        public readonly detections: readonly DetectionResult[],
        public readonly names: Readonly<Record<number, string>>,
        public readonly origImg: RGBImage,
        public readonly origShape: readonly [number, number],
        public readonly path: string | null = null,
        public readonly speed: Readonly<Speed> = NO_SPEED,
    ) {}

    /** Number of surviving detections. */
    get length(): number {
        return this.detections.length;
    }

    /** Index into the per-instance detections. */
    get(index: number): DetectionResult | undefined {
        return this.detections[index];
    }

    [Symbol.iterator](): Iterator<DetectionResult> {
        return this.detections[Symbol.iterator]();
    }
}

/**
 * Per-image envelope for a fused detect→classify pipeline.
 *
 * Structurally a {@link DetectionResults} with a second class map: every
 * detection it yields carries a populated `classification`, and the two stages
 * have their own, unrelated label spaces — a detector that finds `sheep`
 * feeding a classifier that answers `famacha_3` shares no class ids with it.
 * Merging them into one `names` record would make `cls` and
 * `classification.cls` look comparable when they are not.
 *
 * ```typescript
 * const result = (await pipeline.predict("flock.jpg"))[0];
 * for (const detection of result) {
 *   console.log(detection.name, detection.conf, detection.classification?.name);
 * }
 * ```
 */
export class DetectClassifyResults implements Iterable<DetectionResult> {
    constructor(
        public readonly boxes: Boxes,
        public readonly detections: readonly DetectionResult[],
        public readonly names: Readonly<Record<number, string>>,
        public readonly classifierNames: Readonly<Record<number, string>>,
        public readonly origImg: RGBImage,
        public readonly origShape: readonly [number, number],
        public readonly path: string | null = null,
        public readonly speed: Readonly<Speed> = NO_SPEED,
    ) {}

    /** Number of surviving detections. */
    get length(): number {
        return this.detections.length;
    }

    /** Index into the per-instance detections. */
    get(index: number): DetectionResult | undefined {
        return this.detections[index];
    }

    [Symbol.iterator](): Iterator<DetectionResult> {
        return this.detections[Symbol.iterator]();
    }
}

/**
 * Per-image classification envelope (Ultralytics-style `Results`).
 */
export class ClassificationResults {
    constructor(
        public readonly probs: Probs,
        public readonly result: ClassificationResult,
        public readonly names: Readonly<Record<number, string>>,
        public readonly origImg: RGBImage,
        public readonly origShape: readonly [number, number],
        public readonly path: string | null = null,
        public readonly speed: Readonly<Speed> = NO_SPEED,
    ) {}

    /** Top-1 class index (Ultralytics-style alias). */
    get cls(): number {
        return this.probs.top1;
    }

    /** Top-1 confidence (Ultralytics-style alias). */
    get conf(): number {
        return this.probs.top1conf;
    }

    /** Top-1 class name. */
    get name(): string {
        return this.names[this.cls] ?? `class_${this.cls}`;
    }

    /** Per-class probability list, sorted descending (legacy field). */
    get probabilities(): readonly ClassificationResult["probabilities"][number][] {
        return this.result.probabilities;
    }
}

/**
 * Per-image instance-segmentation envelope (Ultralytics-style `Results`).
 *
 * Iterating yields per-instance {@link SegmentationResult} entries. `boxes`
 * and `masks` mirror Ultralytics' bulk-array views.
 */
export class SegmentationResults implements Iterable<SegmentationResult> {
    constructor(
        public readonly boxes: Boxes,
        public readonly masks: Masks,
        public readonly detections: readonly SegmentationResult[],
        public readonly names: Readonly<Record<number, string>>,
        public readonly origImg: RGBImage,
        public readonly origShape: readonly [number, number],
        public readonly path: string | null = null,
        public readonly speed: Readonly<Speed> = NO_SPEED,
    ) {}

    /** Number of surviving instances. */
    get length(): number {
        return this.detections.length;
    }

    /** Index into the per-instance results. */
    get(index: number): SegmentationResult | undefined {
        return this.detections[index];
    }

    [Symbol.iterator](): Iterator<SegmentationResult> {
        return this.detections[Symbol.iterator]();
    }
}
